// src/pages/Budget_entering/RowFormModal.jsx
// Форма ввода строки отчёта — одна страница, четыре смысловых блока подряд.
// Пошагового мастера нет (СТПО задаёт состав полей, а не разбиение по экранам).
// Нумерация блоков сквозная с полем step ответа 422: сервер сообщает, в каком
// блоке ошибка, — форма подсвечивает его заголовок и прокручивает к полю.
//
// Контроллер не отдаёт GET /rows/{id} — правка заполняется из объекта строки,
// уже полученного списком (проп editRow), без отдельного запроса.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Select, Button, notification } from 'antd';
import { useTranslation } from 'react-i18next';

import { createRow, updateRow, ApiError } from './api';
import { REF_SECTIONS, NO_EXEC_SECT, NUMF } from './mockData';
import { fmtPct, fmtResid, parseAmount, toInput } from './format';

const { Option } = Select;

const isRef = (sec) => REF_SECTIONS.indexOf(sec) !== -1;
const isNoExec = (sec) => NO_EXEC_SECT.indexOf(sec) !== -1;
const has = (v) => !(v === null || v === undefined || v === '');

// Поля по блокам (для распределения ошибок и подсветки заголовков).
const BLOCK_FIELDS = {
  1: ['section', 'codes', 'name'],
  2: ['ab', 'ub', 'adj'],
  3: ['pp', 'cp', 'ca', 'ua'],
  4: ['ex'],
};
const fieldBlock = (f) =>
  Number(Object.keys(BLOCK_FIELDS).find((b) => BLOCK_FIELDS[b].includes(f))) || 1;

const EMPTY = {
  sec: 'EXP',
  code1: '',
  code2: '',
  code3: '',
  code4: '',
  code5: '',
  code6: '',
  name: '',
  name_missing: false,
  ab: '',
  ub: '',
  adj: '',
  pp: '',
  cp: '',
  ca: '',
  ua: '',
  ex: '',
};

// Сырые числовые поля ответа API → короткие имена формы (см. NUMF в mockData.js).
const API_NUMF = {
  ab: 'approved_budget',
  ub: 'updated_budget',
  adj: 'adjusted_budget',
  pp: 'payment_plan',
  cp: 'commitment_plan',
  ca: 'committed_amount',
  ua: 'unpaid_amount',
  ex: 'budget_execution_amount',
};

export default function RowFormModal({
  open,
  editRow,
  sections,
  budget,
  period,
  tolerance,
  onSaved,
  onClose,
}) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || '').toLowerCase().startsWith('kk') ? 'kk' : 'ru';

  const editId = editRow ? editRow.id : null;
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({}); // { field: 'ключ' | 'eNoParent:код' | текст }
  const [touched, setTouched] = useState({}); // { blockNo: true }
  const [saveTried, setSaveTried] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const bodyRef = useRef(null);

  /* ------------------------------- загрузка -------------------------------- */
  // Правки заполняются из уже загруженной списком строки — GET /rows/{id} в API нет.
  const loadRow = (r) => {
    const f = { ...EMPTY, sec: r.section, name: r.name || '', name_missing: !!r.name_missing };
    for (let i = 1; i <= 6; i += 1) f[`code${i}`] = r[`code${i}`] || '';
    NUMF.forEach((k) => {
      const v = r[API_NUMF[k]];
      f[k] = has(v) ? toInput(v) : '';
    });
    setForm(f);
    setDirty(false);
  };

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setTouched({});
    setSaveTried(false);
    setSubmitting(false);
    setDirty(false);
    if (editRow) loadRow(editRow);
    else setForm(EMPTY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editRow]);

  /* ------------------------------- признаки -------------------------------- */
  const activeBlocks = useMemo(() => (isRef(form.sec) ? [1, 4] : [1, 2, 3, 4]), [form.sec]);
  const requiredFields = useMemo(() => {
    if (isNoExec(form.sec)) return ['section'];
    if (isRef(form.sec)) return ['section', 'ex'];
    return ['section', 'ab', 'ub', 'adj', 'pp', 'ex'];
  }, [form.sec]);

  const level = useMemo(
    () => [1, 2, 3, 4, 5, 6].filter((i) => has(form[`code${i}`])).length,
    [form]
  );

  // Живой предпросмотр процентов — обратная связь оператору. Авторитетные
  // значения на сохранении считает сервер (см. постановку, «без вычислений»).
  const numVal = (k) => {
    const v = parseAmount(form[k]);
    return v == null || Number.isNaN(v) ? null : v;
  };
  const p1 = useMemo(() => {
    if (isRef(form.sec)) return null;
    const ex = numVal('ex');
    const pp = numVal('pp');
    return ex != null && pp != null && pp !== 0 ? (ex / pp) * 100 : null;
  }, [form.ex, form.pp, form.sec]);
  const p2 = useMemo(() => {
    if (isRef(form.sec)) return null;
    const ex = numVal('ex');
    const adj = numVal('adj');
    return ex != null && adj != null && adj !== 0 ? (ex / adj) * 100 : null;
  }, [form.ex, form.adj, form.sec]);

  /* --------------------------------- патч ---------------------------------- */
  const markTouched = (field) => setTouched((prev) => ({ ...prev, [fieldBlock(field)]: true }));

  const patch = (next, field) => {
    setDirty(true);
    setForm((prev) => ({ ...prev, ...next }));
    if (field) {
      markTouched(field);
      clearError(field);
    }
  };
  const clearError = (field) =>
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const cp = { ...prev };
      delete cp[field];
      return cp;
    });

  // Смена раздела. Переход на справочный при заполненных суммах — с подтверждением
  // (в отчёте этих колонок у справочных строк нет; сохранять их скрытыми нельзя).
  const onSectionChange = (sec) => {
    const goingRef = isRef(sec) && !isRef(form.sec);
    if (goingRef) {
      const dirtyAmt = ['ab', 'ub', 'adj', 'pp', 'cp', 'ca', 'ua'].some((f) => has(form[f]));
      // eslint-disable-next-line no-alert
      if (dirtyAmt && !window.confirm(t('budgetEntering.refClearAsk'))) return; // отмена — раздел не меняем
      setForm((prev) => ({
        ...prev,
        sec,
        ab: '',
        ub: '',
        adj: '',
        pp: '',
        cp: '',
        ca: '',
        ua: '',
      }));
    } else {
      setForm((prev) => ({ ...prev, sec }));
    }
    setDirty(true);
    markTouched('section');
    clearError('section');
  };

  const onNoName = (checked) => {
    setForm((prev) => ({ ...prev, name_missing: checked, name: checked ? '' : prev.name }));
    setDirty(true);
    markTouched('name');
    clearError('name');
  };

  /* ------------------------------ валидация -------------------------------- */
  // Клиентская проверка «для скорости» — не заменяет серверную. Дубли и
  // родительский узел проверяет сервер (422 со step).
  const validate = (all) => {
    const e = {};
    const want = (block) => all || touched[block];

    // Блок 1
    if (want(1)) {
      if (!form.sec) e.section = 'eReq';
      const codes = [1, 2, 3, 4, 5, 6].map((i) => form[`code${i}`].trim());
      const firstEmpty = codes.findIndex((c) => !has(c));
      if (firstEmpty !== -1 && codes.slice(firstEmpty).some(has)) e.codes = 'eGap';
      else if (codes.some((c) => has(c) && c.length > 10)) e.codes = 'eLen10';
      else if (codes.some((c) => has(c) && !/^[0-9A-Za-z]+$/.test(c))) e.codes = 'eCodeFmt';
      if (!form.name_missing && !has(form.name.trim())) e.name = 'eReq';
      else if (form.name.length > 500) e.name = 'eLen500';
    }
    // Блоки 2–4 (числа), только применимые к разделу
    [2, 3, 4].forEach((block) => {
      if (activeBlocks.indexOf(block) === -1 || !want(block)) return;
      BLOCK_FIELDS[block].forEach((f) => {
        if (!NUMF.includes(f)) return;
        const raw = form[f];
        if (!has(raw)) {
          if (requiredFields.includes(f)) e[f] = 'eReq';
          return;
        }
        if (Number.isNaN(parseAmount(raw))) e[f] = 'eNum';
      });
    });
    return e;
  };

  // Пересчёт ошибок по мере правки (только для тронутых блоков).
  useEffect(() => {
    if (!open) return;
    if (!Object.keys(touched).length && !saveTried) return;
    setErrors((prev) => {
      // сохраняем серверные тексты, наложенные на конкретные поля
      const client = validate(saveTried);
      return { ...client };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, touched, saveTried, open]);

  const errText = (field) => {
    const v = errors[field];
    if (!v) return '';
    if (v.startsWith && v.startsWith('eNoParent:'))
      return t('budgetEntering.eNoParent', { p: v.slice('eNoParent:'.length) });
    const known = ['eReq', 'eNum', 'eGap', 'eLen10', 'eCodeFmt', 'eLen500', 'eDup', 'eNoParent'];
    return known.includes(v) ? t(`budgetEntering.${v}`) : v; // иначе — серверный текст как есть
  };
  const showErr = (field) => {
    const b = fieldBlock(field);
    return errors[field] && (saveTried || touched[b]);
  };
  const fieldCls = (field) => `bp-field${showErr(field) ? ' bp-field--err' : ''}`;
  const blockBad = (block) => saveTried && BLOCK_FIELDS[block].some((f) => errors[f]);

  /* ----------------------------- авто-плашки ------------------------------- */
  const rowChecks = useMemo(() => {
    const ca = numVal('ca');
    const ua = numVal('ua');
    const ex = numVal('ex');
    const pp = numVal('pp');
    const adj = numVal('adj');
    const ub = numVal('ub');
    const items = [];
    if (ca != null && ua != null && ex != null) {
      const d = ca - ua - ex;
      items.push({
        kind: Math.abs(d) <= (tolerance || 0.001) ? 'ok' : 'no',
        text: t('budgetEntering.chkOblig'),
        fmla: 'ПО − НО = И',
        resid: d,
      });
    }
    if (ex != null && pp != null && pp === 0 && ex !== 0)
      items.push({
        kind: 'warn',
        text: t('budgetEntering.chkOffPlan'),
        fmla: 'план = 0, исполнение ≠ 0',
      });
    if (adj != null && ub != null && adj === 0 && ub !== 0)
      items.push({
        kind: 'warn',
        text: t('budgetEntering.chkAdjZero'),
        fmla: 'скорр. = 0, уточн. ≠ 0',
      });
    if (ex != null && ex < 0)
      items.push({ kind: 'warn', text: t('budgetEntering.chkNeg'), fmla: 'исполнение < 0' });
    if (!items.length) items.push({ kind: 'ok', text: t('budgetEntering.chkNone') });
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.ca, form.ua, form.ex, form.pp, form.adj, form.ub, tolerance]);

  /* ------------------------------- сохранение ------------------------------ */
  const buildPayload = () => {
    const p = {
      budget,
      date: period,
      sec: form.sec,
      name: form.name.trim(),
      name_missing: form.name_missing,
    };
    for (let i = 1; i <= 6; i += 1) p[`code${i}`] = form[`code${i}`].trim();
    NUMF.forEach((f) => {
      p[f] = has(form[f]) ? parseAmount(form[f]) : null;
    });
    return p;
  };

  const scrollFirst = () =>
    setTimeout(() => {
      const el = bodyRef.current && bodyRef.current.querySelector('.bp-field--err');
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 0);

  const save = async () => {
    if (submitting) return;
    setSaveTried(true);
    // все применимые блоки — тронуты
    const tAll = {};
    activeBlocks.forEach((b) => {
      tAll[b] = true;
    });
    setTouched(tAll);

    const e = validate(true);
    if (Object.keys(e).length) {
      setErrors(e);
      scrollFirst();
      notification.error({ message: t('budgetEntering.toastSaveErr'), placement: 'bottomRight' });
      return;
    }

    setSubmitting(true);
    try {
      const res = editId
        ? await updateRow(editId, buildPayload())
        : await createRow(buildPayload());
      onSaved(res);
    } catch (err) {
      handleSaveError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveError = (err) => {
    const status = err instanceof ApiError ? err.status : 0;
    if (status === 409) {
      notification.warning({
        message: t('budgetEntering.toastConflict'),
        placement: 'bottomRight',
        btn: (
          <Button
            size="small"
            type="primary"
            onClick={() => {
              notification.destroy();
              onClose();
            }}
          >
            {t('budgetEntering.refresh')}
          </Button>
        ),
      });
      return;
    }
    // Реальный контракт: { errors: [{ field, code, message, message_ru, message_kk, step }] }.
    if (status === 422 && err.body && Array.isArray(err.body.errors)) {
      const map = {};
      let unknown = false;
      err.body.errors.forEach((e) => {
        const f = e.field;
        const msg = (lang === 'kk' ? e.message_kk : e.message_ru) || e.message;
        if (f && (BLOCK_FIELDS[1].includes(f) || NUMF.includes(f))) map[f] = msg;
        else unknown = true;
      });
      if (Object.keys(map).length) setErrors((prev) => ({ ...prev, ...map }));
      if (unknown)
        notification.error({ message: t('budgetEntering.toastSaveErr'), placement: 'bottomRight' });
      scrollFirst();
      return;
    }
    notification.error({ message: t('budgetEntering.toastNet'), placement: 'bottomRight' });
  };

  /* -------------------------------- закрытие ------------------------------- */
  const guardClose = () => {
    // eslint-disable-next-line no-alert
    if (dirty && !window.confirm(t('budgetEntering.closeAsk'))) return;
    onClose();
  };

  if (!open) return null;

  const refMode = isRef(form.sec);
  const cnt = form.name.length;

  const amount = (field, hintKey, required) => (
    <div className={fieldCls(field)} id={`w_${field}`}>
      <label>
        {t(`budgetEntering.l${field.charAt(0).toUpperCase()}${field.slice(1)}`)}
        {required && <span className="bp-req">*</span>}
      </label>
      <input
        className="bp-inp num"
        inputMode="decimal"
        value={form[field]}
        onChange={(ev) => patch({ [field]: ev.target.value }, field)}
      />
      <span className="bp-hint">
        {t(`budgetEntering.h${field.charAt(0).toUpperCase()}${field.slice(1)}`)}
      </span>
      {showErr(field) && <span className="bp-msg">{errText(field)}</span>}
    </div>
  );

  return (
    <div
      className="bp-overlay"
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) guardClose();
      }}
    >
      <div className="bp-modal" role="dialog" aria-modal="true">
        <div className="bp-modal-h">
          <h3>{editId ? t('budgetEntering.mRowEdit') : t('budgetEntering.mRowCreate')}</h3>
          <button className="bp-modal-x" onClick={guardClose} aria-label="close">
            ✕
          </button>
        </div>

        <div className="bp-modal-b" ref={bodyRef}>
          {refMode && (
            <div
              className="bp-note"
              style={{ marginBottom: 14 }}
              dangerouslySetInnerHTML={{ __html: t('budgetEntering.refFormNote') }}
            />
          )}

          {/* Блок 1 — раздел и код */}
          <div className="bp-formsec">
            <div className={`bp-sectitle${blockBad(1) ? ' bad' : ''}`}>
              {t('budgetEntering.s1Title')}
            </div>
            <div className="bp-grid2" style={{ marginBottom: 14 }}>
              <div className={fieldCls('section')} id="w_section">
                <label>
                  {t('budgetEntering.lSection')}
                  <span className="bp-req">*</span>
                </label>
                <Select
                  showSearch
                  optionFilterProp="children"
                  value={form.sec}
                  onChange={onSectionChange}
                  style={{ width: '100%' }}
                >
                  {sections.map((s) => (
                    <Option key={s.k} value={s.k}>
                      {lang === 'kk' ? s.kk : s.ru}
                    </Option>
                  ))}
                </Select>
                <span className="bp-hint">{t('budgetEntering.hSection')}</span>
                {showErr('section') && <span className="bp-msg">{errText('section')}</span>}
              </div>
              <div className="bp-field">
                <label>{t('budgetEntering.lLevel')}</label>
                <input className="bp-inp" readOnly tabIndex={-1} value={level} />
                <span className="bp-hint">{t('budgetEntering.hLevel')}</span>
              </div>
            </div>

            <div className={fieldCls('codes')} id="w_codes" style={{ marginBottom: 14 }}>
              <label>{t('budgetEntering.lCodes')}</label>
              <div className="bp-grid6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <input
                    key={i}
                    className="bp-inp"
                    maxLength={10}
                    placeholder={['01', '01', '112', '001', '015', '111'][i - 1]}
                    value={form[`code${i}`]}
                    onChange={(ev) => patch({ [`code${i}`]: ev.target.value }, 'codes')}
                  />
                ))}
              </div>
              <span className="bp-hint">{t('budgetEntering.hCodes')}</span>
              {showErr('codes') && <span className="bp-msg">{errText('codes')}</span>}
            </div>

            <div className={fieldCls('name')} id="w_name">
              <label>
                {t('budgetEntering.lName')}
                <span className="bp-req">*</span>
              </label>
              <input
                className="bp-inp"
                maxLength={500}
                disabled={form.name_missing}
                value={form.name}
                onChange={(ev) => patch({ name: ev.target.value }, 'name')}
              />
              <span className="bp-cnt">{cnt} / 500</span>
              <label className="bp-check-inline">
                <input
                  type="checkbox"
                  checked={form.name_missing}
                  onChange={(ev) => onNoName(ev.target.checked)}
                />
                <span>{t('budgetEntering.lNoName')}</span>
              </label>
              <span className="bp-hint">{t('budgetEntering.hName')}</span>
              {showErr('name') && <span className="bp-msg">{errText('name')}</span>}
            </div>
          </div>

          {/* Блок 2 — годовой бюджет (скрыт для справочных разделов) */}
          {!refMode && (
            <div className="bp-formsec">
              <div className={`bp-sectitle${blockBad(2) ? ' bad' : ''}`}>
                {t('budgetEntering.s2Title')}
              </div>
              <div className="bp-grid3">
                {amount('ab', null, true)}
                {amount('ub', null, true)}
                {amount('adj', null, true)}
              </div>
              <div className="bp-note" style={{ margin: '16px 0 0' }}>
                {t('budgetEntering.s2Note')}
              </div>
            </div>
          )}

          {/* Блок 3 — план и обязательства (скрыт для справочных разделов) */}
          {!refMode && (
            <div className="bp-formsec">
              <div className={`bp-sectitle${blockBad(3) ? ' bad' : ''}`}>
                {t('budgetEntering.s3Title')}
              </div>
              <div className="bp-grid2">
                {amount('pp', null, true)}
                {amount('cp', null, false)}
                {amount('ca', null, false)}
                {amount('ua', null, false)}
              </div>
            </div>
          )}

          {/* Блок 4 — исполнение и контроль */}
          <div className="bp-formsec">
            <div className={`bp-sectitle${blockBad(4) ? ' bad' : ''}`}>
              {t('budgetEntering.s4Title')}
            </div>
            <div className="bp-grid3">
              <div className={fieldCls('ex')} id="w_ex">
                <label>
                  {t('budgetEntering.lEx')}
                  {requiredFields.includes('ex') && <span className="bp-req">*</span>}
                </label>
                <input
                  className="bp-inp num"
                  inputMode="decimal"
                  value={form.ex}
                  onChange={(ev) => patch({ ex: ev.target.value }, 'ex')}
                />
                <span className="bp-hint">{t('budgetEntering.hEx')}</span>
                {showErr('ex') && <span className="bp-msg">{errText('ex')}</span>}
              </div>
              <div className="bp-field">
                <label>{t('budgetEntering.lP1')}</label>
                <input
                  className="bp-inp num"
                  readOnly
                  tabIndex={-1}
                  value={refMode ? '—' : fmtPct(p1)}
                />
                <span className="bp-hint">
                  {refMode ? t('budgetEntering.hРrefPct') : t('budgetEntering.hP1')}
                </span>
              </div>
              <div className="bp-field">
                <label>{t('budgetEntering.lP2')}</label>
                <input
                  className="bp-inp num"
                  readOnly
                  tabIndex={-1}
                  value={refMode ? '—' : fmtPct(p2)}
                />
                <span className="bp-hint">
                  {refMode ? t('budgetEntering.hРrefPct') : t('budgetEntering.hP2')}
                </span>
              </div>
            </div>

            <div className="bp-sectitle" style={{ marginTop: 20 }}>
              {t('budgetEntering.s4Checks')}
            </div>
            <div>
              {rowChecks.map((c, i) => (
                <div className={`bp-chk ${c.kind}`} key={i}>
                  <span className="ic">{c.kind === 'ok' ? '✓' : c.kind === 'no' ? '✗' : '!'}</span>
                  <span>
                    {c.text}
                    {c.fmla && <span className="fmla"> {c.fmla}</span>}
                  </span>
                  {c.resid != null && (
                    <span className="res">
                      {t('budgetEntering.resid')}: {fmtResid(c.resid)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bp-modal-f">
          <Button onClick={guardClose} disabled={submitting}>
            {t('budgetEntering.cancel')}
          </Button>
          <Button type="primary" onClick={save} loading={submitting}>
            {t('budgetEntering.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}
